/* eslint-disable max-len */
import React, { useState, useEffect, useContext, useRef } from "react";
import GlobalContext from "../../components/Context/GloablContext";
import { dataService } from "../../services/data.services";
import AddHoliday from "./AddHoliday";
import FeeStructureDropdown from "../../components/FeeStructureDropdown";
import Toast from "../../components/Toast";
import Loader from "../../components/Loader";
import CommonModal from "../../components/Modal";
import { GetBranchId } from '../../CommonMethods/GetBranchId';
import ItemShimmer from "../../components/Shimmer/itemShimmer";
import {
    formatDateLabel,
    formatDayLabel,
    getHolidayTypeLabel,
    isAcademicYearCompleted,
    isHolidayPast,
    normalizeHolidayItems,
} from "./CommonMethods";

export default function StudentHoliday() {

    const [branchId, setBranchId] = useState(null);

    const [academicYears, setAcademicYears] = useState([]); // was ""
    const [classes, setClasses] = useState([]); // was ""
    const [sections, setSections] = useState([]);

    const [selectedAcademicYear, setSelectedAcademicYear] = useState(null);
    const [selectedClass, setSelectedClass] = useState(null);
    const [selectedSection, setSelectedSection] = useState(null);

    const [academicYearLoading, setAcademicYearLoading] = useState(false);
    const [sectionLoading, setSectionLoading] = useState(false);
    const [fetching, setFetching] = useState(false);

    const [selectedDate, setSelectedDate] = useState(null);
    const [selectedHolidayData, setSelectedHolidayData] = useState(null);
    const [modalInitialScreen, setModalInitialScreen] = useState("view");
    const [holidays, setHolidays] = useState([]);
    const [holidaySectionNames, setHolidaySectionNames] = useState({});
    const [holidaySectionStatuses, setHolidaySectionStatuses] = useState({});
    const [appliedFilters, setAppliedFilters] = useState({
        academicYear: null,
        classItem: null,
        section: null,
        classLabel: null,
    });
    const [appliedSections, setAppliedSections] = useState([]);
    const [pendingDeleteAction, setPendingDeleteAction] = useState(null);
    const [deleteSelection, setDeleteSelection] = useState([]);
    const [expandedDeleteClasses, setExpandedDeleteClasses] = useState({});
    const [deleteLoading, setDeleteLoading] = useState(false);
    const initialFetchDoneRef = useRef(false);

    const [toastErrorMessage, setToastErrorMessage] = useState("");
    const { setToastmessage, isInactiveBranch } = useContext(GlobalContext);

    // ---- Get branch ID on mount ----
    useEffect(() => {
        let mounted = true;
        GetBranchId()
            .then((id) => {
                if (mounted) setBranchId(id);
            })
            .catch((err) => {
                console.error('Failed to get branch id', err);
                if (mounted) setToastErrorMessage("Failed to determine branch");
            });
        return () => { mounted = false; };
    }, []);

    // ---- Dropdown change handlers ----
    const onAcademicYearChange = (ay) => {
        setSelectedAcademicYear(ay || null);
        setSelectedClass(null);
        setSelectedSection(null);
        setSections([]);
    };
    const onClassChange = (clz) => {
        if (clz?.__all) {
            setSelectedClass(clz);
            setSelectedSection(null);
            setSections([]);
            return;
        }
        setSelectedClass(clz || null);
    };
    const onSectionChange = (sec) => {
        if (sec?.__all) {
            setSelectedSection(sec);
            return;
        }
        setSelectedSection(sec || null);
    };

    const hasClassFilter = !!appliedFilters.classItem?._id;
    const hasSectionFilter = !!appliedFilters.section?.id;
    const showAppliesToColumn = !!appliedFilters.academicYear?._id && !hasSectionFilter;
    const showBubble = showAppliesToColumn && !hasClassFilter;

    const hasClasses = (classes || []).length > 0;
    const hasSections = (sections || []).length > 0;
    const classOptions = [
        ...(hasClasses ? [{ name: "All Classes", _id: null, __all: true }] : []),
        ...(classes || []),
    ];
    const sectionOptions = [
        ...(hasSections ? [{ name: "All Sections", id: null, __all: true }] : []),
        ...(sections || []),
    ];
    const classEmptyLabel = hasClasses ? "All Classes" : "No classes";
    const sectionEmptyLabel = (selectedClass && selectedClass._id) ? (hasSections ? 'All Sections' : 'No Sections') : (hasClasses ? 'All Sections' : 'No Sections');

    const getScopeHeaderText = () => {
        if (!appliedFilters.academicYear?.name) return "";
        if (hasClassFilter && hasSectionFilter) {
            return `Viewing holidays for: ${appliedFilters.academicYear.name} > ${appliedFilters.classItem?.name || ""} > ${appliedFilters.section?.name || ""}`.trim();
        }
        if (hasClassFilter) {
            return `Viewing holidays for: ${appliedFilters.academicYear.name} > ${appliedFilters.classItem?.name || ""}`.trim();
        }
        if (appliedFilters.classLabel) {
            return `Viewing holidays for: ${appliedFilters.academicYear.name} > ${appliedFilters.classLabel}`;
        }
        return `Viewing holidays for: ${appliedFilters.academicYear.name}`;
    };

    const activeClassIds = new Set(
        (classes || []).filter((c) => c?.status !== false).map((c) => c?._id).filter(Boolean)
    );
    const classNameById = new Map((classes || []).map((c) => [c?._id, c?.name]));
    const normalizeId = (value) => (value ? String(value) : "");
    
    const sectionsById = new Map([
        ...(appliedSections || []).flatMap((s) => {
            const id = normalizeId(s?.id || s?.section_id);
            const name = s?.name || s?.section_name;
            return id ? [[id, name]] : [];
        }),
        ...Object.entries(holidaySectionNames || {}).map(([id, name]) => [normalizeId(id), name]),
    ]);
    const sectionStatusById = new Map([
        ...(appliedSections || []).flatMap((s) => {
            const id = normalizeId(s?.id || s?.section_id);
            return id ? [[id, s?.status]] : [];
        }),
        ...Object.entries(holidaySectionStatuses || {}).map(([id, status]) => [
            normalizeId(id),
            status,
        ]),
    ]);

    const resolveSectionName = (section) => {
        
        const id = normalizeId(section.section_id || section.id);
        return sectionsById.get(id) || section?.section_name
    };

    const resolveClassName = (holiday) => {
        
        const id = normalizeId(holiday?.class_id || holiday?.id);
        return (
            classNameById.get(id)
        );
    };

    const getHolidayModificationBlockMessage = () => {
        if (isAcademicYearCompleted(selectedAcademicYear)) {
            return "AY Completed. Hence Holidays cannot be modified";
        }
        return "";
    };

    const getHolidayActionBlockMessage = (item, action) => {
        if (action?.key !== "edit" && isHolidayPast(item)) {
            const actionLabel = String(action?.label || "This action").toLowerCase();
            return `Past Holiday, ${actionLabel.charAt(0).toUpperCase() + actionLabel.slice(1)} is not allowed`;
        }
        return "";
    };

    const getHolidayScope = (item) => String(item?.holiday?.holiday_scope || "").toUpperCase();
    const getHolidayScopeLabel = (item) => {
        const scope = getHolidayScope(item);
        const scopeLabels = {
            BRANCH: "Branch",
            "STUDENT-AY": "AY",
            "STUDENT-CLASS": "Class",
            "STUDENT-SECTION": "Section",
        };
        return scopeLabels[scope] || "-";
    };
    const getFilterScopeKey = () => {
        if (hasClassFilter && hasSectionFilter) return "CLASS_SECTION";
        if (hasClassFilter) return "CLASS_ALL_SECTIONS";
        return "AY_ALL_CLASSES";
    };

    const getHolidayIdentifierPayload = (holiday) => ({
        branch_id: branchId,
        holiday_range_id: holiday?.holiday_range_id || null,
        holiday_main_id: holiday?.holiday_main_id || null,
        branch_holiday_id: holiday?.branch_holiday_id || null,
    });

    const buildClassOptions = (item) => {
        const seen = new Set();
        return (item?.appliedToEntries || []).flatMap((entry) => {
            const classId = normalizeId(entry?.class_id);
            if (!classId || seen.has(classId)) return [];
            seen.add(classId);
            return [{
                id: classId,
                label: resolveClassName(entry) || entry?.class_name || classId,
            }];
        });
    };

    const buildSectionOptions = (item, classId) => {
        const seen = new Set();
        return (item?.appliedToEntries || []).flatMap((entry) => {
            const entryClassId = normalizeId(entry?.class_id);
            const sectionId = normalizeId(entry?.section_id);
            const rowId = normalizeId(entry?._id);

            if (!sectionId || !entryClassId || !rowId || (classId && entryClassId !== normalizeId(classId))) {
                return [];
            }
            if (seen.has(rowId)) return [];
            seen.add(rowId);
            return [{
                id: rowId,
                label: resolveSectionName(entry) || sectionId,
                classId: entryClassId,
                sectionId,
            }];
        });
    };

    const buildFixedDeleteScope = (classes = [], sections = []) => ({
        classes: classes.filter(Boolean),
        sections: sections.filter(Boolean),
    });

    const buildExcludeHolidayList = (item, { rowIds = [], classes = [], sections = [] } = {}) => {
        const rowIdSet = new Set((rowIds || []).map(normalizeId).filter(Boolean));
        const classSet = new Set((classes || []).map(normalizeId).filter(Boolean));
        const sectionSet = new Set((sections || []).map(normalizeId).filter(Boolean));

        return Array.from(
            new Set(
                (item?.appliedToEntries || [])
                    .filter((entry) => {
                        const rowId = normalizeId(entry?._id);
                        const classId = normalizeId(entry?.class_id);
                        const sectionId = normalizeId(entry?.section_id);

                        if (rowIdSet.size > 0) {
                            return rowIdSet.has(rowId);
                        }

                        if (sectionSet.size > 0) {
                            return sectionSet.has(sectionId)
                                && (classSet.size === 0 || classSet.has(classId));
                        }

                        if (classSet.size > 0) {
                            return classSet.has(classId);
                        }

                        return false;
                    })
                    .map((entry) => entry?._id)
                    .filter(Boolean)
            )
        );
    };

    const buildClassHierarchy = (item) => (
        buildClassOptions(item).map((classOption) => ({
            ...classOption,
            sections: buildSectionOptions(item, classOption.id),
        }))
    );

    const resolveRowActions = (item) => {
        const holiday = item?.holiday;
        const holidayScope = getHolidayScope(item);
        const filterScope = getFilterScopeKey();
        const actions = [];
        const actionDateLabel = formatDateLabel(item);

        if (!holiday || !holidayScope) return actions;

        const rowClassOptions = buildClassOptions(item);
        const firstClassId = normalizeId(holiday?.class_id) || rowClassOptions[0]?.id || null;
        const rowSectionOptions = buildSectionOptions(item, firstClassId);
        const selectedClassId = selectedClass?._id || null;
        const selectedSectionId = selectedSection?.id || null;

        if (filterScope === "AY_ALL_CLASSES") {
            if (holidayScope === "BRANCH") {
                actions.push({
                    key: "exclude-class",
                    label: "Exclude Classes",
                    dialogTitle: `Exclude Classes: ${actionDateLabel}`,
                    type: "delete",
                    apiMode: "exclude",
                    selectionMode: "class",
                    options: rowClassOptions,
                    confirmationText: "Select the classes to exclude from the holiday"
                });
            }
            if (holidayScope === "STUDENT-AY") {
                actions.push({ key: "edit", label: "Edit Holiday", dialogTitle: `Edit Holiday: ${actionDateLabel}`, type: "edit" });
                actions.push({
                    key: "delete",
                    label: "Delete Holiday",
                    dialogTitle: `Delete Holiday: ${actionDateLabel}`,
                    type: "delete",
                    apiMode: "delete",
                    selectionMode: "fixed",
                    fixedScope: buildFixedDeleteScope(),
                    confirmationText: "Do you want to delete this holiday for all classes and sections?",
                });
                actions.push({
                    key: "exclude-class",
                    label: "Exclude Classes",
                    dialogTitle: `Exclude Classes: ${actionDateLabel}`,
                    type: "delete",
                    apiMode: "exclude",
                    selectionMode: "class",
                    options: rowClassOptions,
                    confirmationText: "Select the classes to exclude from the holiday",
                });
            }
            if (holidayScope === "STUDENT-CLASS") {
                actions.push({ key: "edit", label: "Edit Holiday", dialogTitle: `Edit Holiday: ${actionDateLabel}`, type: "edit" });
                actions.push({
                    key: "delete",
                    label: "Delete Holiday",
                    dialogTitle: `Delete Holiday: ${actionDateLabel}`,
                    type: "delete",
                    apiMode: "delete",
                    selectionMode: "fixed",
                    fixedScope: buildFixedDeleteScope([holiday?.class_id]),
                    confirmationText: "Do you want to delete this holiday for the class: "+resolveClassName(holiday)+" and its sections?",
                });
                actions.push({
                    key: "exclude-section",
                    label: "Exclude Sections",
                    dialogTitle: `Exclude Sections: ${actionDateLabel}`,
                    type: "delete",
                    apiMode: "exclude",
                    selectionMode: "section",
                    classId: firstClassId,
                    options: rowSectionOptions,
                    confirmationText: "Select the sections to exclude from the holiday",
                });
            }
            if (holidayScope === "STUDENT-SECTION") {
                actions.push({ key: "edit", label: "Edit Holiday", dialogTitle: `Edit Holiday: ${actionDateLabel}`, type: "edit" });
                actions.push({
                    key: "delete",
                    label: "Delete Holiday",
                    dialogTitle: `Delete Holiday: ${actionDateLabel}`,
                    type: "delete",
                    apiMode: "delete",
                    selectionMode: "fixed",
                    fixedScope: buildFixedDeleteScope([holiday?.class_id], [holiday?.section_id]),
                    confirmationText: "Do you want to delete this holiday for the section '"+resolveSectionName(holiday)+"'?",
                });
            }
        }

        if (filterScope === "CLASS_ALL_SECTIONS") {
            if (holidayScope === "BRANCH" || holidayScope === "STUDENT-AY") {
                actions.push({
                    key: "exclude-section",
                    label: "Exclude Sections",
                    dialogTitle: `Exclude Sections: ${actionDateLabel}`,
                    type: "delete",
                    apiMode: "exclude",
                    selectionMode: "section",
                    classId: selectedClassId,
                    options: buildSectionOptions(item, selectedClassId),
                    confirmationText: "Select the sections to exclude from the holiday",
                });
            }
            if (holidayScope === "STUDENT-CLASS") {
                actions.push({ key: "edit", label: "Edit Holiday", dialogTitle: `Edit Holiday: ${actionDateLabel}`, type: "edit" });
                actions.push({
                    key: "delete",
                    label: "Delete Holiday",
                    dialogTitle: `Delete Holiday: ${actionDateLabel}`,
                    type: "delete",
                    apiMode: "delete",
                    selectionMode: "fixed",
                    fixedScope: buildFixedDeleteScope([selectedClassId || holiday?.class_id]),
                    confirmationText: "Do you want to delete this holiday for the class: "+resolveClassName(holiday)+" and its sections?",
                });
                actions.push({
                    key: "exclude-section",
                    label: "Exclude Sections",
                    dialogTitle: `Exclude Sections: ${actionDateLabel}`,
                    type: "delete",
                    apiMode: "exclude",
                    selectionMode: "section",
                    classId: selectedClassId,
                    options: buildSectionOptions(item, selectedClassId),
                    confirmationText: "Select the sections to exclude from the holiday",
                });
            }
            if (holidayScope === "STUDENT-SECTION") {
                actions.push({ key: "edit", label: "Edit Holiday", dialogTitle: `Edit Holiday: ${actionDateLabel}`, type: "edit" });
                actions.push({
                    key: "delete",
                    label: "Delete Holiday",
                    dialogTitle: `Delete Holiday: ${actionDateLabel}`,
                    type: "delete",
                    apiMode: "delete",
                    selectionMode: "fixed",
                    fixedScope: buildFixedDeleteScope(
                        [selectedClassId || holiday?.class_id],
                        [holiday?.section_id]
                    ),
                    confirmationText: "Do you want to delete this holiday for the section '"+resolveSectionName(holiday)+"'?",
                });
            }
        }

        if (filterScope === "CLASS_SECTION") {
            if (holidayScope === "BRANCH" || holidayScope === "STUDENT-AY" || holidayScope === "STUDENT-CLASS") {
                actions.push({
                    key: "exclude-section",
                    label: "Exclude Sections",
                    dialogTitle: `Exclude Sections: ${actionDateLabel}`,
                    type: "delete",
                    apiMode: "exclude",
                    selectionMode: "fixed",
                    fixedScope: buildFixedDeleteScope([selectedClassId], [selectedSectionId]),
                    confirmationText: "Do you want to exclude the section '" + selectedSection?.name + "' from this holiday?",
                });
            }
            if (holidayScope === "STUDENT-SECTION") {
                actions.push({ key: "edit", label: "Edit Holiday", dialogTitle: `Edit Holiday: ${actionDateLabel}`, type: "edit" });
                actions.push({
                    key: "delete",
                    label: "Delete Holiday",
                    dialogTitle: `Delete Holiday: ${actionDateLabel}`,
                    type: "delete",
                    apiMode: "delete",
                    selectionMode: "fixed",
                    fixedScope: buildFixedDeleteScope([selectedClassId], [selectedSectionId]),
                    confirmationText: "Do you want to delete this holiday for the section '"+resolveSectionName(holiday)+"'?",
                });
            }
        }

        return actions.filter((action) => {
            if (action.type !== "delete") return true;
            if (action.selectionMode === "class") return (action.options || []).length > 0;
            if (action.selectionMode === "section") {
                return !!action.classId && (action.options || []).length > 0;
            }
            if (action.selectionMode === "fixed") {
                return true;
            }
            return false;
        });
    };

    const studentHolidayRows = [...(holidays || [])]
        .sort((a, b) =>
            (a?.start_date || a?.date || "").localeCompare(
                b?.start_date || b?.date || ""
            )
        );

    const getAppliesToText = (item) => {
        if (!showAppliesToColumn) return "";
        const entries = item?.appliedToEntries || [];

        if (!hasClassFilter) {
            const classIds = new Set(
                entries.map((h) => h?.class_id).filter(Boolean)
            );
            
            const count = classIds.size;
            return `${count} Classes`;
        }

        const names = entries.map((h) => {
            const id = normalizeId(h?.section_id);
            const label = resolveSectionName(h);
            const inactive = sectionStatusById.get(id) === false;
            return inactive ? `${label} (Inactive)` : label;
        });
        const uniq = Array.from(new Set(names));
        return uniq.join(", ");
    };

    const popoverRef = useRef(null);
    const triggerRef = useRef(null);
    const [openPopoverKey, setOpenPopoverKey] = useState(null);
    const [popoverStyle, setPopoverStyle] = useState({});
    const [popoverItem, setPopoverItem] = useState(null);
    const actionMenuRef = useRef(null);
    const actionTriggerRef = useRef(null);
    const [openActionMenuKey, setOpenActionMenuKey] = useState(null);

    const closePopover = () => {
        setOpenPopoverKey(null);
        setPopoverItem(null);
        if (triggerRef.current) {
            triggerRef.current.focus();
        }
    };

    const closeActionMenu = () => {
        setOpenActionMenuKey(null);
        if (actionTriggerRef.current) {
            actionTriggerRef.current.focus();
        }
    };

    useEffect(() => {
        if (!openPopoverKey && !openActionMenuKey) return;

        const handleOutside = (event) => {
            const clickedOutsidePopover = (
                popoverRef.current &&
                !popoverRef.current.contains(event.target) &&
                triggerRef.current &&
                !triggerRef.current.contains(event.target)
            );
            const clickedOutsideActionMenu = (
                actionMenuRef.current &&
                !actionMenuRef.current.contains(event.target) &&
                actionTriggerRef.current &&
                !actionTriggerRef.current.contains(event.target)
            );

            if (openPopoverKey && clickedOutsidePopover) {
                closePopover();
            }
            if (openActionMenuKey && clickedOutsideActionMenu) {
                closeActionMenu();
            }
        };

        const handleEsc = (event) => {
            if (event.key === "Escape") {
                closePopover();
                closeActionMenu();
            }
        };

        document.addEventListener("mousedown", handleOutside);
        document.addEventListener("keydown", handleEsc);
        return () => {
            document.removeEventListener("mousedown", handleOutside);
            document.removeEventListener("keydown", handleEsc);
        };
    }, [openPopoverKey, openActionMenuKey]);

    useEffect(() => {
        closePopover();
        closeActionMenu();
    }, [selectedAcademicYear?._id, selectedClass?._id, selectedSection?.id]);

    useEffect(() => {
        if (openPopoverKey && popoverRef.current) {
            popoverRef.current.focus();
        }
    }, [openPopoverKey]);

    const buildPopoverStyle = (rect) => {
        const maxWidth = 520;
        const padding = 12;
        const top = rect.bottom + 8;
        let left = rect.left;
        const viewportWidth = window.innerWidth;
        if (left + maxWidth + padding > viewportWidth) {
            left = Math.max(padding, viewportWidth - maxWidth - padding);
        }
        return {
            position: "fixed",
            top,
            left,
            maxWidth: `${maxWidth}px`,
            zIndex: 60,
        };
    };

    const handleAppliesToClick = (item, key) => (event) => {
        if (!showBubble) return;
        if (openPopoverKey === key) {
            closePopover();
            return;
        }
        triggerRef.current = event.currentTarget;
        setPopoverItem(item);
        setOpenPopoverKey(key);
        const rect = event.currentTarget.getBoundingClientRect();
        setPopoverStyle(buildPopoverStyle(rect));
    };

    const handleActionMenuClick = (key) => (event) => {
        event.stopPropagation();
        const item = (holidays || []).find((holidayItem) => {
            const rowKey = holidayItem?.id || holidayItem?.date || `${holidayItem?.start_date}-${holidayItem?.end_date}`;
            return rowKey === key;
        });
        const blockMessage = getHolidayModificationBlockMessage();
        if (blockMessage) {
            closeActionMenu();
            setToastErrorMessage(blockMessage);
            return;
        }
        if (openActionMenuKey === key) {
            closeActionMenu();
            return;
        }
        actionTriggerRef.current = event.currentTarget;
        setOpenActionMenuKey(key);
    };

    const buildClassApplicability = (item) => {
        const entries = item?.appliedToEntries || [];
        const classIds = Array.from(
            new Set(entries.map((h) => h?.class_id).filter(Boolean))
        );

        const map = new Map();
        classIds.forEach((id) => map.set(id, { sections: new Map() }));

        entries.forEach((h) => {
            const classId = h?.class_id;
            if (!classId) return;
            if (!map.has(classId)) map.set(classId, { sections: new Map() });
            const entry = map.get(classId);
            if (h?.section_id) {
                const id = normalizeId(h?.section_id);
                const name = resolveSectionName(h);
                const inactive = sectionStatusById.get(id) === false;
                entry.sections.set(name, inactive);
            }
        });

        return Array.from(map.entries()).map(([classId, entry]) => {
            const className = resolveClassName({ class_id: classId });
            const inactive = classId && !activeClassIds.has(classId);
            const sections = Array.from(entry.sections.entries())
                .filter(([name]) => name)
                .map(([name, inactive]) => (inactive ? `${name} (Inactive)` : name))
                .join(", ");
            return {
                classId,
                className,
                sections: sections,
            };
        });
    };

    // Section-level popover details removed; sections are shown directly in the list.

    // ---- API: Academic Years ----
    const getAcademicYears = async () => {
        try {
            setAcademicYearLoading(true);
            const endpoint = `academic-years?branch_id=${branchId}&filter=true&status=ongoing,upcoming,completed`;
            const response = await dataService.GetAPI(endpoint);
            const temp = response?.data?.data?.data || [];
            setAcademicYears(temp);// If no academic year selected yet, default to the first 'ongoing' AY.
            if (!selectedAcademicYear) {
                const ongoing = temp.find((ay) => ay?.status === 'ongoing');
                if (ongoing) {
                    setSelectedAcademicYear(ongoing);
                } else if (temp.length > 0) {
                    // fallback to first available AY if none ongoing
                    setSelectedAcademicYear(temp[0]);
                } else {
                    setSelectedAcademicYear(null);
                }
            }
            return temp;
        } catch (error) {
            console.log(error);
            setToastErrorMessage(
                error?.response?.data?.message || "Something went wrong"
            );
            return [];
        } finally {
            setAcademicYearLoading(false);
        }
    };

    // ---- API: Classes ----
    const getClasses = async () => {
        try {
            const endpoint = `classes?branch_id=${branchId}&sort=status%2Basc&role=students-attendance`;
            const response = await dataService.GetAPI(endpoint, true);
            const temp = response?.data?.data?.data || [];
            const normalized = temp.map((cls) => {
                if (cls?.status === false && !String(cls?.name || "").includes("(Inactive)")) {
                    return {
                        ...cls,
                        name: `${cls?.name || ""} (Inactive)`.trim(),
                    };
                }
                return cls;
            });
            setClasses(normalized);
        } catch (error) {
            console.log(error);
            setToastErrorMessage(
                error?.response?.data?.message || "Failed to load classes"
            );
        }
    };

    // ---- API: Sections master list (for names in table) ----
    const getSections = async () => {
        try {
            const endpoint = `sections?branch_id=${branchId}&sort=status%2Basc&role=students-attendance`;
            const response = await dataService.GetAPI(endpoint, true);
            const temp = response?.data?.data?.data || [];
            const nameMap = {};
            const statusMap = {};
            temp.forEach((obj) => {
                const id = normalizeId(obj?._id);
                if (id && obj?.name) {
                    nameMap[id] = obj.name;
                }
                if (id && Object.prototype.hasOwnProperty.call(obj || {}, "status")) {
                    statusMap[id] = obj.status;
                }
            });
            return { nameMap, statusMap };
        } catch (error) {
            console.log(error);
            return { nameMap: {}, statusMap: {} };
        }
    };

    // ---- API: Sections (depends on AY + Class) ----
    const callAssociationsAPI = async () => {
        setSectionLoading(true);
        try {
            const endpoint = `associations?branch_id=${branchId}&academic_year_id=${selectedAcademicYear._id}&class_id=${selectedClass._id}&filter=true&role=students-attendance`;
            const response = await dataService.GetAPI(endpoint, true);
            const temp = response?.data?.data?.data || [];
            setSections(
                temp.map((obj) => {
                    return {
                        name: obj.section_name,
                        id: obj.section_id,
                        subjects: obj.subjects,
                    };
                })
            );
        } catch (error) {
            console.log(error);
            setToastErrorMessage(
                error?.response?.data?.message || "Failed to load sections"
            );
            setSections([]);
        } finally {
            setSectionLoading(false);
        }
    };

    // ---- API: Holidays list for calendar ----
    const getHolidays = async () => {
        if (!selectedAcademicYear?._id) {
            setToastErrorMessage("Please select Academic Year");
            return;
        }

        try {
            setFetching(true);

            const params = new URLSearchParams({
                branch_id: branchId,
                academic_year_id: selectedAcademicYear._id,
            });
            if (selectedClass?._id) params.append("class_id", selectedClass._id);
            if (selectedSection?.id)
                params.append("section_id", selectedSection.id);

            const endpoint = `holidays/list-holidays?${params.toString()}&is_student_holiday=true`;
            const response = await dataService.GetAPI(endpoint, true);

            if (response?.data?.status === 200) {
                const data = normalizeHolidayItems(response?.data?.data || {}, {
                    idKeys: ["holiday_main_id", "branch_holiday_id", "holiday_range_id"],
                    includeAppliedToEntries: true,
                });
                const sectionMaster = await getSections();
                setHolidays(data);
                const sectionNameMap = {};
                data.forEach((item) => {
                    (item.appliedToEntries || []).forEach((entry) => {
                        const id = normalizeId(entry?.section_id);
                        const name = entry?.section_name;
                        if (id && name) {
                            sectionNameMap[id] = name;
                        }
                    });
                });
                setHolidaySectionStatuses(sectionMaster.statusMap || {});
                setHolidaySectionNames({
                    ...sectionNameMap,
                    ...(sectionMaster.nameMap || {}),
                });
                setAppliedFilters({
                    academicYear: selectedAcademicYear || null,
                    classItem: selectedClass?.__all ? null : selectedClass || null,
                    section: selectedSection?.__all ? null : selectedSection || null,
                    classLabel: selectedClass?.__all ? "All Classes" : null,
                });
                setAppliedSections(
                    selectedClass?.__all || !selectedClass?._id ? [] : sections || []
                );
            } else {
                setHolidays([]);
            }
        } catch (error) {
            console.error("Error fetching holidays:", error);
            setHolidays([]);
            setToastErrorMessage(
                error?.response?.data?.message || "Failed to fetch holidays"
            );
        } finally {
            setFetching(false);
        }
    };

    // ---- Lifecycle ----
    useEffect(() => {
        if (!branchId) return;
        getAcademicYears();
        getClasses();
    }, [branchId]);

    useEffect(() => {
    // When both selected, fetch sections
        if (selectedAcademicYear?._id && selectedClass?._id) {
            callAssociationsAPI();
        } else {
            setSections([]);
            setSelectedSection(null);
        }
    }, [selectedAcademicYear?._id, selectedClass?._id]);

    useEffect(() => {
        if (selectedAcademicYear?._id && !initialFetchDoneRef.current) {
            initialFetchDoneRef.current = true;
            getHolidays();
        }
    }, [selectedAcademicYear?._id]);

    // ---- Modal control ----
    const [modalOpen, setModalOpen] = useState(false);
    const handleAddHolidayButton = () => {
        if(!selectedAcademicYear) {
            setToastErrorMessage("Please select Academic Year");
            return;
        }
        if (isAcademicYearCompleted(selectedAcademicYear)) {
            setToastErrorMessage("AY Completed, Holidays cannot be added");
            return;
        }
        setSelectedDate(null);
        setSelectedHolidayData(null);
        setModalInitialScreen("view");
        //setSelectedDate(toLocalStart(new Date()));
        setModalOpen(true);
    };

    const fetchHolidayDetails = async (item) => {
        const holiday = item?.holiday;
        if (!holiday || !selectedAcademicYear?._id || !branchId) return null;

        const params = new URLSearchParams({
            branch_id: branchId,
            academic_year_id: selectedAcademicYear._id,
        });

        if (holiday?.holiday_range_id) {
            params.append("holiday_range_id", holiday.holiday_range_id);
        } else if (holiday?.holiday_main_id) {
            params.append("holiday_main_id", holiday.holiday_main_id);
        } else if (holiday?.branch_holiday_id) {
            params.append("branch_holiday_id", holiday.branch_holiday_id);
        } else {
            return null;
        }

        const endpoint = `holidays/view?${params.toString()}`;
        const response = await dataService.GetAPI(endpoint, true);
        if (response?.data?.status !== 200) {
            return null;
        }

        const holidayData = response?.data?.data?.holiday || null;
        if (!holidayData) return null;

        holidayData.holiday_range_id = holiday?.holiday_range_id || holidayData?.holiday_range_id || null;
        holidayData.holiday_main_id = holiday?.holiday_main_id || holidayData?.holiday_main_id || null;
        holidayData.branch_holiday_id = holiday?.branch_holiday_id || holidayData?.branch_holiday_id || null;
        return holidayData;
    };

    const buildHolidayDataFromItem = (item) => {
        const holiday = item?.holiday;
        if (!holiday) return null;

        return {
            ...holiday,
            start_date: item?.start_date || item?.date || holiday?.start_date || null,
            end_date: item?.end_date || item?.date || holiday?.end_date || null,
            holiday_range_id: holiday?.holiday_range_id || item?.date_range_id || null,
            holiday_main_id: holiday?.holiday_main_id || null,
            branch_holiday_id: holiday?.branch_holiday_id || null,
        };
    };

    const handleEditHoliday = (item) => {
        const blockMessage = getHolidayActionBlockMessage(item, { key: "edit" });
        if (blockMessage) {
            setToastErrorMessage(blockMessage);
            return;
        }

        const holidayData = buildHolidayDataFromItem(item);
        if (!holidayData) {
            setToastErrorMessage("Failed to load holiday details");
            return;
        }

        setSelectedDate(item?.date || item?.start_date || null);
        setSelectedHolidayData(holidayData);
        setModalInitialScreen("edit");
        setModalOpen(true);
    };

    const openDeleteActionModal = (item, action) => {
        const blockMessage = getHolidayActionBlockMessage(item, action);
        if (blockMessage) {
            setToastErrorMessage(blockMessage);
            return;
        }

        const defaultSelection = [];
        const defaultExpandedClasses = {};

        if (action?.selectionMode === "class") {
            buildClassHierarchy(item).forEach((classItem) => {
                defaultExpandedClasses[classItem.id] = false;
            });
        }

        if (action?.selectionMode === "section" && (action?.options || []).length === 1) {
            defaultSelection.push(action.options[0].id);
        }

        setPendingDeleteAction({ ...action, item });
        setDeleteSelection(defaultSelection);
        setExpandedDeleteClasses(defaultExpandedClasses);
    };

    const closeDeleteActionModal = () => {
        if (deleteLoading) return;
        setPendingDeleteAction(null);
        setDeleteSelection([]);
        setExpandedDeleteClasses({});
    };

    const toggleDeleteSelection = (id) => {
        setDeleteSelection((prev) => (
            prev.includes(id)
                ? prev.filter((itemId) => itemId !== id)
                : [...prev, id]
        ));
    };

    const toggleDeleteClassExpanded = (classId) => {
        setExpandedDeleteClasses((prev) => ({
            ...prev,
            [classId]: !prev[classId],
        }));
    };

    const toggleDeleteClassSelection = (classId, sectionIds) => {
        if (!sectionIds.length) return;

        setDeleteSelection((prev) => {
            const allSelected = sectionIds.every((sectionId) => prev.includes(sectionId));
            if (allSelected) {
                return prev.filter((id) => !sectionIds.includes(id));
            }
            const next = new Set(prev);
            sectionIds.forEach((sectionId) => next.add(sectionId));
            return Array.from(next);
        });

        setExpandedDeleteClasses((prev) => ({
            ...prev,
            [classId]: true,
        }));
    };

    const submitDeleteAction = async () => {
        if (!pendingDeleteAction) return;
        const blockMessage = getHolidayActionBlockMessage(
            pendingDeleteAction.item,
            pendingDeleteAction
        );
        if (blockMessage) {
            setToastErrorMessage(blockMessage);
            closeDeleteActionModal();
            return;
        }

        const holiday = pendingDeleteAction.item?.holiday;
        if (!holiday) {
            setToastErrorMessage("Holiday details are missing");
            return;
        }

        let classes = [];
        let sections = [];
        let rowIds = [];

        if (pendingDeleteAction.selectionMode === "class") {
            if (!deleteSelection.length) {
                setToastErrorMessage("Select at least one section");
                return;
            }
            const hierarchy = buildClassHierarchy(pendingDeleteAction.item);
            classes = hierarchy
                .filter((classItem) => classItem.sections.some((section) => deleteSelection.includes(section.id)))
                .map((classItem) => classItem.id);
            rowIds = deleteSelection;
        }

        if (pendingDeleteAction.selectionMode === "section") {
            if (!deleteSelection.length) {
                setToastErrorMessage("Select at least one section");
                return;
            }
            classes = [pendingDeleteAction.classId];
            rowIds = deleteSelection;
        }

        if (pendingDeleteAction.selectionMode === "fixed") {
            classes = pendingDeleteAction.fixedScope?.classes || [];
            sections = pendingDeleteAction.fixedScope?.sections || [];
        }

        try {
            setDeleteLoading(true);
            let response;

            if (pendingDeleteAction.apiMode === "exclude") {
                const excludeHolidayList = buildExcludeHolidayList(
                    pendingDeleteAction.item,
                    { rowIds, classes, sections }
                );

                if (!excludeHolidayList.length) {
                    setToastErrorMessage("No holiday entries found to exclude");
                    setDeleteLoading(false);
                    return;
                }

                const body = {
                    branch_id: branchId,
                    academic_year_id: selectedAcademicYear._id,
                    exclude_holiday_list: excludeHolidayList,
                };
                response = await dataService.PutAPI(body, "holidays/exclude-class-section");
            } else {
                const body = {
                    ...getHolidayIdentifierPayload(holiday),
                    academic_year_id: selectedAcademicYear._id,
                };
                response = await dataService.PutAPI(body, "holidays/delete");
            }

            if (!response.error) {
                closeDeleteActionModal();
                await getHolidays();
                setTimeout(() => setToastmessage?.("Holiday updated successfully"), 100);
            } else {
                setToastErrorMessage(response?.data?.message || "Failed to update holiday");
            }
        } catch (error) {
            console.error("Error applying holiday delete action:", error);
            setToastErrorMessage(
                error?.response?.data?.message || "Failed to update holiday"
            );
        } finally {
            setDeleteLoading(false);
        }
    };

    return (
        <div className="bg-white">
            {/* Controls row */}
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 my-2 items-end border p-3 rounded sticky top-0 z-20 bg-white">
                {/* Academic Year */}
                <div className="flex flex-col">
                    <label className="text-f7 font-normal text-[#031624] mb-1 inline-flex">
                        Academic Year<span className="req-fld-astrk">*</span>
                    </label>
                    <FeeStructureDropdown
                        dropDownName={"academic_year"}
                        id={"name"}
                        test_id={"year_name"}
                        value={selectedAcademicYear?.name || "Select Academic Year"}
                        placeHolder={"Select Academic Year"}
                        isMandatoryField={true}
                        options={academicYears}
                        handleOnchangeInput={(key, value) => onAcademicYearChange(value)}
                        loading={academicYearLoading}
                        buttonDisabled={academicYearLoading}
                    />
                </div>

                {/* Class */}
                <div className="flex flex-col">
                    <label className="text-f7 font-normal text-[#031624] mb-1 inline-flex">
                        Class
                    </label>
                    <FeeStructureDropdown
                        dropDownName={"class_name"}
                        value={selectedClass?.name || classEmptyLabel}
                        divClassName={"relative w-full"}
                        buttonDisabled={!selectedAcademicYear?._id}
                        applyBGForDisabled={true}
                        placeHolder={"Select Class"}
                        options={classOptions}
                        handleOnchangeInput={(key, value) => onClassChange(value)}
                        toottip={false}
                    />
                </div>

                {/* Section */}
                <div className="flex flex-col">
                    <label className="text-f7 font-normal text-[#031624] mb-1 inline-flex">
                        Section
                    </label>
                    <FeeStructureDropdown
                        dropDownName={"section"}
                        value={selectedSection?.name || sectionEmptyLabel}
                        divClassName={"relative w-full"}
                        buttonDisabled={!selectedAcademicYear?._id || !selectedClass?._id}
                        placeHolder={"Select Section"}
                        applyBGForDisabled={true}
                        options={sectionOptions}
                        handleOnchangeInput={(key, value) => onSectionChange(value)}
                        toottip={false}
                        loading={sectionLoading}
                    />
                </div>

                {/* Actions (span 2 cols on md+) */}
                <div className="flex md:col-span-2 justify-end gap-3">
                    <button
                        type="button"
                        onClick={getHolidays}
                        style={{'min-width': '120px'}}
                        disabled={academicYearLoading || fetching}
                        className={`view-actn-btn`}
                    >
                        {academicYearLoading || fetching ? <Loader />  : "Apply Filter"}
                    </button>

                    {!isInactiveBranch &&
                        <button
                            type="button"
                            onClick={handleAddHolidayButton}
                            aria-disabled={isAcademicYearCompleted(selectedAcademicYear)}
                            className={`prmy-btn onboardInstitution ${isAcademicYearCompleted(selectedAcademicYear) ? "opacity-60 cursor-not-allowed" : ""}`}
                        >
                            Add Holiday(s)
                        </button>
                    }   
                </div>
            </div>

            {/*holidays list in table*/}
            <div className="mt-4">
                {appliedFilters.academicYear?._id && (
                    <div id="compare-text" className="w-full text-base font-normal text-[#031624] mb-[5px]">
                        {getScopeHeaderText()}
                    </div>
                )}
                <div className={`flex mb-3 overflow-auto ${holidays.length < 10 ? 'pb-[100px]' : 'pb-[6px]'} px-1 scroll-bar max-h-[calc(100vh-280px)]`} id="search-result">
                    {fetching ?
                        <ItemShimmer classkey={""} />
                        :
                        <>
                            <table id="admin-table" class="stickyhead">
                                <thead className="bg-[#f3f4f6] text-[#031624]">
                                    <tr>
                                        <th className="w-[13%] text-left">Date</th>
                                        <th className="w-[13%] text-left">Day</th>
                                        <th className="w-[20%] text-left">Holiday Name</th>
                                        <th className="w-[12%] text-left">Type</th>
                                        {showAppliesToColumn && (
                                            <th className="w-[20%] text-left">Applies To</th>
                                        )}
                                        <th className="w-[12%] text-left">Holiday Scope</th>
                                        <th className="w-[7%] text-center">Actions</th>
                                    </tr>
                                </thead>
                                {studentHolidayRows.length === 0 ? (
                                    <tbody>
                                        <tr>
                                            <td style={{ lineHeight: '100px' }} colSpan={showAppliesToColumn ? 7 : 6} className="no-records-message" id='no-matching-result'>
                                                {'No records found.'}
                                            </td>
                                        </tr>
                                    </tbody>
                                ) :
                                    <tbody>
                                        {studentHolidayRows.map((item) => {
                                            const rowKey = item?.id || item?.date || `${item?.start_date}-${item?.end_date}`;
                                            const actions = resolveRowActions(item);

                                            return (
                                                <tr key={rowKey} className="border-t">
                                                    <td className="px-4 py-2">{formatDateLabel(item)}</td>
                                                    <td className="px-4 py-2">{formatDayLabel(item)}</td>
                                                    <td className="px-4 py-2">
                                                        {item?.holiday?.name || "-"}
                                                    </td>
                                                    <td className="px-4 py-2">{getHolidayTypeLabel(item)}</td>
                                                    {showAppliesToColumn && (
                                                        <td className="px-4 py-2">
                                                            {hasClassFilter ? (
                                                                <span>{getAppliesToText(item)}</span>
                                                            ) : (
                                                                <button
                                                                    type="button"
                                                                    className={`text-left w-full cursor-pointer hover:underline ${openPopoverKey === rowKey ? "underline" : ""}`}
                                                                    onClick={handleAppliesToClick(item, rowKey)}
                                                                    aria-haspopup="dialog"
                                                                    aria-expanded={openPopoverKey === rowKey}
                                                                >
                                                                    {getAppliesToText(item)}
                                                                </button>
                                                            )}
                                                        </td>
                                                    )}
                                                    <td className="px-4 py-2">
                                                        {getHolidayScopeLabel(item)}
                                                    </td>
                                                    <td className="px-4 py-2">
                                                        <div className="relative flex items-center justify-center prevent-click" data-te-dropdown-position="dropstart">
                                                            {actions.length === 0 ? (
                                                                <button type="button" className="list-more-btn prevent-click disabled">
                                                                    <img src="/images/listAction.svg" alt="Actions" />
                                                                </button>
                                                            ) : (
                                                                <>
                                                                    <button
                                                                        type="button"
                                                                        disabled={isInactiveBranch}
                                                                        className={`list-more-btn prevent-click ${isInactiveBranch?"img-disable":""}`}
                                                                        onClick={handleActionMenuClick(rowKey)}
                                                                        aria-haspopup="menu"
                                                                        aria-expanded={openActionMenuKey === rowKey}
                                                                    >
                                                                        <img src="/images/listAction.svg" alt="Actions" />
                                                                    </button>
                                                                    {openActionMenuKey === rowKey && (
                                                                        <ul
                                                                            ref={actionMenuRef}
                                                                            className="list-more-optns-div [&[data-te-dropdown-show]]:block prevent-click" aria-labelledby="dropdownMenuButton2" data-te-dropdown-menu-ref
                                                                            role="menu"
                                                                        >
                                                                            {actions.map((action) => (
                                                                                <button
                                                                                    key={`${rowKey}-${action.key}`}
                                                                                    type="button"
                                                                                    className={`list-more-optns prevent-click`}
                                                                                    onClick={() => {
                                                                                        closeActionMenu();
                                                                                        if (action.type === "edit") {
                                                                                            handleEditHoliday(item);
                                                                                            return;
                                                                                        }
                                                                                        openDeleteActionModal(item, action);
                                                                                    }}
                                                                                    role="menuitem"
                                                                                >
                                                                                    {action.label}
                                                                                </button>
                                                                            ))}
                                                                        </ul>
                                                                    )}
                                                                </>
                                                            )}
                                                        </div>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                }
                            </table>
                        </>}
                </div>
                {showBubble && openPopoverKey && popoverItem && (
                    <div
                        ref={popoverRef}
                        tabIndex={-1}
                        style={popoverStyle}
                        className="bg-white rounded-lg shadow-lg border p-3 max-h-[320px] overflow-auto focus:outline-none"
                        role="dialog"
                        aria-label="Holiday Applicability Details"
                    >
                        {!hasClassFilter && (
                            <div>
                                <div className="text-sm font-semibold text-[#031624] mb-2">
                                    Holiday Applicability Details
                                </div>
                                <div className="overflow-auto">
                                    <table className="min-w-full text-xs">
                                        <thead className="text-left text-[#031624]">
                                            <tr>
                                                <th className="py-1 pr-3 font-semibold">Class</th>
                                                <th className="py-1 pr-3 font-semibold">Section(s)</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {buildClassApplicability(popoverItem).map((row) => (
                                                <tr key={row.classId || row.className}>
                                                    <td className="py-1 pr-3">
                                                        {row.className}
                                                    </td>
                                                    <td className="py-1 pr-3">{row.sections}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        )}
                    </div>
                )}
            </div>


            {/* Modal */}
            <AddHoliday
                academicYear={selectedAcademicYear}
                classItem={selectedClass?.__all ? null : selectedClass}
                section={selectedSection?.__all ? null : selectedSection}
                holidayScope="STUDENT"
                setToastmessage={setToastmessage}
                setToastErrorMessage={setToastErrorMessage}
                open={modalOpen}
                branch_id={branchId}
                onClose={() => {
                    setModalOpen(false);
                    setSelectedDate(null);
                    setSelectedHolidayData(null);
                    setModalInitialScreen("view");
                }}
                getHolidays={getHolidays}
                selectedDate={selectedDate} // clicked date at local midnight
                holidayData={selectedHolidayData}
                initialEditModeScreen={modalInitialScreen}
                allowInlineDelete={false}
            />
            {pendingDeleteAction && (
                <CommonModal
                    show={true}
                    handleClose={closeDeleteActionModal}
                    title={pendingDeleteAction.dialogTitle || pendingDeleteAction.label}
                    className="font-normal p-4"
                    dialogueWidth="560px"
                    dialogueHeight="fit-content"
                    dialogTopOffset={25}
                >
                    <div className="px-4 py-2">
                        <p className="text-sm text-[#344054]">
                            {pendingDeleteAction.confirmationText}
                        </p>
                        {pendingDeleteAction.selectionMode !== "fixed" && (
                            <div className="mt-4 max-h-[220px] overflow-auto">
                                {pendingDeleteAction.selectionMode === "class" ? (
                                    buildClassHierarchy(pendingDeleteAction.item).map((classItem) => {
                                        const sectionIds = classItem.sections.map((section) => section.id);
                                        const selectedSectionCount = sectionIds.filter((sectionId) => deleteSelection.includes(sectionId)).length;
                                        const allSectionsSelected = sectionIds.length > 0 && selectedSectionCount === sectionIds.length;
                                        const someSectionsSelected = selectedSectionCount > 0 && !allSectionsSelected;

                                        return (
                                            <div key={classItem.id} className="mb-3 rounded border border-[#EAECF0]">
                                                <div className="flex items-start gap-2 px-3 py-1">
                                                    <input
                                                        type="checkbox"
                                                        checked={allSectionsSelected}
                                                        ref={(el) => {
                                                            if (el) {
                                                                el.indeterminate = someSectionsSelected;
                                                            }
                                                        }}
                                                        onChange={() => toggleDeleteClassSelection(classItem.id, sectionIds)}
                                                        className="mt-2"
                                                    />
                                                    <div className="flex-1 cursor-pointer" onClick={() => toggleDeleteClassExpanded(classItem.id)}>
                                                        <div className="font-medium text-[#101828]">{classItem.label}</div>
                                                        <div className="text-xs text-[#667085]">
                                                            {selectedSectionCount}/{sectionIds.length} sections selected
                                                        </div>
                                                    </div>
                                                    
                                                </div>
                                                {expandedDeleteClasses[classItem.id] && (
                                                    <div className="bg-[#F9FAFB] px-6 py-2">
                                                        {classItem.sections.map((section) => (
                                                            <label key={section.id} className="flex items-center gap-2 py-1 text-sm text-[#101828]">
                                                                <input
                                                                    type="checkbox"
                                                                    checked={deleteSelection.includes(section.id)}
                                                                    onChange={() => toggleDeleteSelection(section.id)}
                                                                />
                                                                <span>{section.label}</span>
                                                            </label>
                                                        ))}
                                                    </div>
                                                )}
                                            </div>
                                        );
                                    })
                                ) : (
                                    (pendingDeleteAction.options || []).map((option) => (
                                        <div key={option.id} className="flex items-start gap-2 px-3 py-1">
                                            <input
                                                type="checkbox"
                                                checked={deleteSelection.includes(option.id)}
                                                onChange={() => toggleDeleteSelection(option.id)}
                                                className="mt-2"
                                            />
                                            <label className="flex flex-col">
                                                <span>{option.label}</span>
                                            </label>
                                        </div>
                                    ))
                                )}
                            </div>
                        )}
                        <div className="flex justify-center gap-3 mt-5">
                            <button
                                type="button"
                                className="cancl-btn1"
                                onClick={closeDeleteActionModal}
                                disabled={deleteLoading}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                className={`actn-btn1 ${deleteLoading ? "opacity-70 cursor-not-allowed" : ""}`}
                                onClick={submitDeleteAction}
                                disabled={deleteLoading}
                            >
                                {deleteLoading ? <Loader /> : pendingDeleteAction.label}
                            </button>
                        </div>
                    </div>
                </CommonModal>
            )}
            {toastErrorMessage!=='' &&<Toast
                message={toastErrorMessage}
                type="error"
                setToastmessage={setToastErrorMessage} />}
        </div>
    );
}
